import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreActions } from '@/store/useStore';
import {
  ArrowLeft, Check, User, BookOpen, Loader2, AlertTriangle, LucideIcon,
} from 'lucide-react';
import { useStudentFormLogic } from '@/hooks/useStudentFormLogic';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SimpleAnimatedText from '@/components/SimpleAnimatedText';
import BlurText from '@/components/BlurText';
import TextType from '@/components/TextType';
import Logo from '@/components/Logo';
import analytics from '@/utils/analytics';
import { CURRENT_ACADEMIC_TERM } from '@/config/academicTerm';

// ─── Primitives ───────────────────────────────────────────────────────────────

function NavBackBtn({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontFamily: "'Outfit',sans-serif",
        color: hov ? '#fff' : 'rgba(255,255,255,.4)',
        background: 'none',
        border: `1px solid ${hov ? 'rgba(255,255,255,.25)' : 'rgba(255,255,255,.1)'}`,
        borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
        transition: 'color .2s, border-color .2s',
      }}
    >
      <ArrowLeft size={13} aria-hidden /> Back
    </button>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{
      display: 'block',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 9, letterSpacing: '.12em',
      color: 'rgba(255,255,255,.35)',
      marginBottom: 7,
    }}>
      {children}
    </label>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 10, color: 'rgba(255,255,255,.25)', marginTop: 5, fontWeight: 300 }}>
      {children}
    </p>
  );
}

function ErrorMsg({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 10, color: 'rgba(239,68,68,.8)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }} role="alert" aria-live="polite">
      <AlertTriangle size={10} aria-hidden /> {children}
    </p>
  );
}

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,.05)',
  border: '1px solid rgba(255,255,255,.09)',
  borderRadius: 9, padding: '10px 14px',
  fontSize: 12, color: '#fff',
  fontFamily: "'Outfit', sans-serif", fontWeight: 300,
  outline: 'none', transition: 'border-color .2s',
};

function StyledInput({ 
  name, 
  type = 'text', 
  placeholder, 
  value, 
  onChange, 
  onFocus, 
  onBlur, 
  focusedField, 
  disabled, 
  'data-testid': testId, 
  ...rest 
}: { 
  name: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  focusedField: string | null;
  disabled?: boolean;
  'data-testid'?: string;
  [key: string]: any;
}) {
  return (
    <input
      name={name} type={type} placeholder={placeholder}
      value={value} onChange={onChange}
      onFocus={onFocus} onBlur={onBlur}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...inputStyle,
        borderColor: focusedField === name ? 'rgba(20,184,166,.5)' : 'rgba(255,255,255,.09)',
      }}
      {...rest}
    />
  );
}

function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 20, paddingBottom: 12,
      borderBottom: '1px solid rgba(255,255,255,.06)',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: 'rgba(20,184,166,.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#14B8A6',
      }}>
        <Icon size={13} aria-hidden />
      </div>
      <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 18, fontWeight: 400, letterSpacing: '-.01em' }}>
        {children}
      </span>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────
const STEPS = [
  { label: 'Personal Info' },
  { label: 'Academic Details' },
  { label: 'Confirm & Go' },
];

function StepList({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {STEPS.map((s, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'pending';
        return (
          <div key={s.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', position: 'relative' }}>
            {i < STEPS.length - 1 && (
              <div style={{
                position: 'absolute', left: 14, top: 38,
                width: 1, height: 'calc(100% - 12px)',
                background: state === 'done' ? 'rgba(20,184,166,.25)' : 'rgba(255,255,255,.08)',
              }} />
            )}
            <div style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'JetBrains Mono',monospace", fontSize: 10,
              background: state === 'active' ? '#14B8A6' : state === 'done' ? 'rgba(20,184,166,.2)' : 'rgba(255,255,255,.04)',
              border: `1px solid ${state === 'active' ? '#14B8A6' : state === 'done' ? 'rgba(20,184,166,.4)' : 'rgba(255,255,255,.1)'}`,
              color: state === 'active' ? '#0A0A0A' : state === 'done' ? '#14B8A6' : 'rgba(255,255,255,.3)',
            }}>
              {state === 'done' ? <Check size={12} aria-hidden /> : i + 1}
            </div>
            <span style={{
              fontSize: 12, paddingTop: 6,
              color: state === 'active' ? '#fff' : state === 'done' ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.3)',
              fontWeight: state === 'active' ? 500 : 400,
            }}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Profile preview sidebar card ────────────────────────────────────────────
function ProfilePreview({ name, category, program, year, semester, group }: { 
  name?: string; 
  category?: string; 
  program?: string; 
  year?: string; 
  semester?: string; 
  group?: string;
}) {
  const rows = [
    { key: 'NAME',     val: name },
    { key: 'CATEGORY', val: category ? `${category} Programs` : null },
    { key: 'PROGRAM',  val: program },
    { key: 'YEAR',     val: year },
    { key: 'SEMESTER', val: semester ? `Semester ${semester}` : null },
    { key: 'GROUP',    val: group ? `Group ${group}` : null },
  ].filter(r => r.val);

  if (!rows.length) return null;

  return (
    <div style={{
      background: 'rgba(20,184,166,.06)',
      border: '1px solid rgba(20,184,166,.15)',
      borderRadius: 12, padding: '16px 18px', marginTop: 20,
    }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', color: 'rgba(20,184,166,.6)', marginBottom: 12 }}>
        PROFILE PREVIEW
      </div>
      {rows.map(r => (
        <div key={r.key} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,.05)', fontSize: 11,
        }}>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.08em', color: 'rgba(255,255,255,.3)' }}>{r.key}</span>
          <span style={{ color: 'rgba(255,255,255,.7)' }}>{r.val}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Welcome screen (unchanged logic, new styles) ────────────────────────────
function WelcomeScreen({ step, name, isReturning }: { step: number; name: string; isReturning: boolean }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A0A0A',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '0 clamp(24px,5vw,80px)',
      position: 'relative', 
      overflow: 'hidden',
    }}>
      {/* Ambient glows */}
      <div style={{ 
        position:'absolute', 
        top:-100, 
        left:-80, 
        width:400, 
        height:400,
        background:'radial-gradient(circle,rgba(20,184,166,.12) 0%,transparent 65%)', 
        pointerEvents:'none' 
      }} />
      <div style={{ 
        position:'absolute', 
        bottom:-80, 
        right:-60, 
        width:320, 
        height:320,
        background:'radial-gradient(circle,rgba(96,165,250,.07) 0%,transparent 65%)', 
        pointerEvents:'none' 
      }} />

      <div style={{ 
        position:'relative', 
        zIndex:1, 
        width:'100%', 
        maxWidth:900, 
        textAlign:'center',
        padding: '0 16px',
      }}>
        {/* ── Mono eyebrow label ── */}
        <div style={{ 
          display:'flex', 
          alignItems:'center', 
          justifyContent:'center', 
          gap:8,
          fontFamily:"'JetBrains Mono',monospace", 
          fontSize:'clamp(8px, 2vw, 10px)', 
          letterSpacing:'.18em',
          color:'rgba(20,184,166,.6)', 
          marginBottom:'clamp(20px, 4vw, 32px)',
          flexWrap: 'wrap',
        }}>
          <div style={{ width:'clamp(20px, 5vw, 32px)', height:1, background:'rgba(20,184,166,.3)' }} />
          {step === 1 ? 'WELCOME' : step === 2 ? (isReturning ? 'SPRING 2026' : 'LET\'S GO') : 'ALL SET'}
          <div style={{ width:'clamp(20px, 5vw, 32px)', height:1, background:'rgba(20,184,166,.3)' }} />
        </div>

        {/* ── Step 1: Hello ── */}
        {step === 1 && (
          <div style={{ width: '100%' }}>
            <p style={{ 
              fontSize:'clamp(12px, 2.5vw, 14px)', 
              color:'rgba(255,255,255,.35)', 
              fontWeight:300,
              letterSpacing:'.02em', 
              marginBottom:'clamp(8px, 2vw, 12px)',
              textAlign: 'center',
            }}>
              You're all set —
            </p>
            <div style={{
              fontFamily:"'Instrument Serif',serif",
              fontSize:'clamp(48px, 10vw, 96px)',
              fontWeight:400,
              letterSpacing:'-.04em', 
              lineHeight:.95,
              color:'#fff',
              textAlign: 'center',
              width: '100%',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}>
              <SimpleAnimatedText
                text={`Hello, ${name}!`}
                className="break-words"
              />
            </div>
            <p style={{ 
              marginTop:'clamp(16px, 3vw, 20px)', 
              fontSize:'clamp(11px, 2.2vw, 13px)', 
              color:'rgba(255,255,255,.25)',
              fontWeight:300, 
              letterSpacing:'.01em',
              textAlign: 'center',
            }}>
              Setting up your dashboard…
            </p>
          </div>
        )}

        {/* ── Step 2: Excitement ── */}
        {step === 2 && (
          <div style={{ width: '100%' }}>
            <div style={{
              fontFamily:"'Instrument Serif',serif",
              fontSize:'clamp(36px, 8vw, 76px)',
              fontWeight:400, 
              letterSpacing:'-.03em', 
              lineHeight:1.1, 
              color:'#fff',
              textAlign: 'center',
              width: '100%',
              wordBreak: 'break-word',
              hyphens: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <BlurText
                text={isReturning ? `Welcome back to ${CURRENT_ACADEMIC_TERM}!` : "Isn't this so cool?!"}
                delay={100} 
                animateBy="words" 
                direction="top"
                threshold={0} 
                rootMargin="0px"
                className="break-words justify-center"
              />
            </div>
            {/* Status badge */}
            <div style={{ 
              display:'flex',
              justifyContent: 'center',
              marginTop:'clamp(16px, 3vw, 24px)',
            }}>
              <div style={{ 
                display:'inline-flex', 
                alignItems:'center', 
                gap:'clamp(4px, 1vw, 6px)', 
                padding:'clamp(5px, 1vw, 6px) clamp(10px, 2vw, 14px)', 
                borderRadius:20,
                background:'rgba(20,184,166,.08)', 
                border:'1px solid rgba(20,184,166,.2)',
                fontFamily:"'JetBrains Mono',monospace", 
                fontSize:'clamp(8px, 1.8vw, 10px)', 
                letterSpacing:'.1em',
                color:'rgba(20,184,166,.7)',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}>
                <div style={{ 
                  width:'clamp(4px, 1vw, 5px)', 
                  height:'clamp(4px, 1vw, 5px)', 
                  borderRadius:'50%', 
                  background:'#14B8A6',
                  flexShrink: 0,
                }} />
                <span style={{ whiteSpace: 'nowrap' }}>UNISCHEDULE IS READY FOR YOU</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Typing message ── */}
        {step === 3 && (
          <div style={{ width: '100%' }}>
            <div style={{
              fontFamily:"'Instrument Serif',serif",
              fontSize:'clamp(24px, 6vw, 52px)',
              fontWeight:400, 
              letterSpacing:'-.02em', 
              lineHeight:1.2,
              color:'rgba(255,255,255,.85)',
              textAlign: 'center',
              width: '100%',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}>
              <TextType
                text={isReturning
                  ? "Your new semester schedule is ready! Let's make this semester amazing!"
                  : "Welcome to UniSchedule! It's great to have you here!"}
                typingSpeed={60} 
                showCursor 
                loop={false}
                initialDelay={0} 
                cursorCharacter="|"
                className="break-words"
              />
            </div>
            <p style={{ 
              marginTop:'clamp(20px, 4vw, 28px)', 
              fontFamily:"'JetBrains Mono',monospace",
              fontSize:'clamp(8px, 1.8vw, 10px)', 
              letterSpacing:'.14em', 
              color:'rgba(255,255,255,.2)',
              textAlign: 'center',
            }}>
              REDIRECTING TO DASHBOARD…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function StudentForm() {
  const navigate = useNavigate();
  const { setUserPreferences } = useStoreActions();
  const [showWelcome, setShowWelcome] = useState(false);
  const [userName, setUserName] = useState('');
  const [animationStep, setAnimationStep] = useState(1);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(true); // Default to true

  // Load saved form data on mount
  useEffect(() => {
    const savedFormData = localStorage.getItem('savedFormData');
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        // Pre-fill form with saved data
        Object.keys(parsed).forEach(key => {
          form.setValue(key as any, parsed[key]);
        });
        // Set the selected values for dropdowns
        if (parsed.category) setSelectedCategory(parsed.category);
        if (parsed.program) setSelectedProgram(parsed.program);
        if (parsed.year) setSelectedYear(parsed.year);
        if (parsed.semester) setSelectedSemester(parsed.semester);
      } catch (e) {
        console.error('Failed to load saved form data:', e);
      }
    }
  }, []);

  const {
    form,
    selectedCategory, setSelectedCategory,
    selectedProgram,  setSelectedProgram,
    selectedYear,     setSelectedYear,
    selectedSemester, setSelectedSemester,
    submitStatus,
    validationErrors,
    isDataLoading,
    availablePrograms, availableYears, availableSemesters, availableGroups,
    clearFieldError, handleFieldChange, resetDependentFields, getDepartmentForProgram,
  } = useStudentFormLogic({
    mode: 'create',
    onSubmit: async (data) => {
      console.log('Form submitted with data:', data);
      const hadPrev = localStorage.getItem('userPreferences') !== null;
      setIsReturningUser(hadPrev);
      await new Promise(r => setTimeout(r, 1500));

      const department = getDepartmentForProgram(data.program);
      const romanToNum: Record<string, number> = { I:1,II:2,III:3,IV:4,V:5,VI:6,VII:7,VIII:8,IX:9,X:10 };
      const semNum = data.semester ? (romanToNum[data.semester] ?? parseInt(data.semester)) : 1;

      setUserPreferences({
        name: data.name?.trim() || '',
        email: data.email?.trim() || '',
        department,
        program: data.program,
        semester: (isNaN(semNum) ? 1 : semNum).toString(),
        group: data.group,
        year: data.year,
      });

      analytics.selectProgram(data.program, data.semester || '1', data.group);
      
      const userName = data.name || 'You';
      console.log('Setting userName to:', userName);
      setUserName(userName);
      setAnimationStep(1);
      console.log('Setting showWelcome to true');
      setShowWelcome(true);
      
      console.log('Starting animation sequence');
      setTimeout(() => {
        console.log('Moving to step 2');
        setAnimationStep(2);
      }, 2500);
      setTimeout(() => {
        console.log('Moving to step 3');
        setAnimationStep(3);
      }, 5000);
      setTimeout(() => {
        console.log('Navigating to dashboard');
        navigate('/dashboard/home');
      }, 12000);
    },
  });

  // Create submit handler using React Hook Form
  const onFormSubmit = form.handleSubmit(async (data) => {
    console.log('onFormSubmit called with:', data);
    
    // Save form data if remember me is checked
    if (rememberMe) {
      localStorage.setItem('savedFormData', JSON.stringify(data));
      console.log('💾 Form data saved for future use');
    } else {
      localStorage.removeItem('savedFormData');
      console.log('🗑️ Saved form data cleared');
    }
    
    const hadPrev = localStorage.getItem('userPreferences') !== null;
    setIsReturningUser(hadPrev);
    await new Promise(r => setTimeout(r, 1500));

    const department = getDepartmentForProgram(data.program);
    const romanToNum: Record<string, number> = { I:1,II:2,III:3,IV:4,V:5,VI:6,VII:7,VIII:8,IX:9,X:10 };
    const semNum = data.semester ? (romanToNum[data.semester] ?? parseInt(data.semester)) : 1;

    setUserPreferences({
      name: data.name?.trim() || '',
      email: data.email?.trim() || '',
      department,
      program: data.program,
      semester: (isNaN(semNum) ? 1 : semNum).toString(),
      group: data.group,
      year: data.year,
    });

    analytics.selectProgram(data.program, data.semester || '1', data.group);
    
    const userName = data.name || 'You';
    console.log('Setting userName to:', userName);
    setUserName(userName);
    setAnimationStep(1);
    console.log('Setting showWelcome to true');
    setShowWelcome(true);
    
    console.log('Starting animation sequence');
    setTimeout(() => {
      console.log('Moving to step 2');
      setAnimationStep(2);
    }, 2500);
    setTimeout(() => {
      console.log('Moving to step 3');
      setAnimationStep(3);
    }, 5000);
    setTimeout(() => {
      console.log('Navigating to dashboard');
      navigate('/dashboard/home');
    }, 12000);
  });

  // ── Welcome screen
  if (showWelcome) {
    return <WelcomeScreen step={animationStep} name={userName} isReturning={isReturningUser} />;
  }

  // Determine sidebar step
  const sidebarStep = !selectedCategory ? 0 : (!selectedProgram || !selectedYear || !selectedSemester || !form.watch('group')) ? 1 : 2;
  const isComplete = form.watch('program') && form.watch('year') && form.watch('semester') && form.watch('group');

  return (
    <div style={{ background: '#0A0A0A', fontFamily: "'Outfit',sans-serif", color: '#fff', minHeight: '100vh' }}>

      {/* ── Nav ─────────────────────────────────────────── */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px clamp(24px,5vw,56px)',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        position: 'sticky', top: 0, zIndex: 50, background: '#0A0A0A',
      }}>
        <Logo size="md" clickable navigateTo="/" scheduleWhite />
        <NavBackBtn onClick={() => navigate('/role-selection')} />
      </header>

      {/* ── Two-col layout ───────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-8 items-start max-w-[1000px] mx-auto px-6 sm:px-10 py-10 sm:py-14">

        {/* ── Sidebar ──────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#14B8A6' }} />
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.18em', color: 'rgba(20,184,166,.7)' }}>SETUP</span>
          </div>
          <h1 style={{ fontFamily: "'Instrument Serif',serif", fontSize: 32, fontWeight: 400, letterSpacing: '-.03em', lineHeight: 1, marginBottom: 10 }}>
            Your{' '}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.3)' }}>academic</em>
            <br />profile
          </h1>
          <p style={{ fontSize: 12, lineHeight: 1.7, color: 'rgba(255,255,255,.35)', fontWeight: 300, marginBottom: 28, maxWidth: 220 }}>
            Fill in your details once. We'll load your personalised timetable every time.
          </p>

          <StepList current={sidebarStep} />

          <ProfilePreview
            name={form.watch('name')}
            category={form.watch('category')}
            program={form.watch('program')}
            year={form.watch('year')}
            semester={form.watch('semester')}
            group={form.watch('group')}
          />
        </div>

        {/* ── Form card ───────────────────────────────── */}
        <div style={{
          background: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(255,255,255,.07)',
          borderRadius: 16, padding: 'clamp(20px, 5vw, 32px)',
        }}>
          <Form {...form}>
            <form onSubmit={onFormSubmit}>

              {/* ── Personal Information ─────────────── */}
              <div style={{ marginBottom: 28 }}>
                <SectionTitle icon={User}>Personal Information</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Name */}
                  <div>
                    <FieldLabel>FULL NAME <span style={{ color: 'rgba(255,255,255,.2)', fontSize: 8 }}>(optional)</span></FieldLabel>
                    <StyledInput
                      name="name" placeholder="Your full name"
                      value={form.watch('name') || ''}
                      focusedField={focusedField}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('name', e.target.value, form.setValue)}
                      data-testid="input-name"
                      disabled={false}
                    />
                    {validationErrors.name && <ErrorMsg>{validationErrors.name}</ErrorMsg>}
                  </div>
                  {/* Email */}
                  <div>
                    <FieldLabel>EMAIL <span style={{ color: 'rgba(255,255,255,.2)', fontSize: 8 }}>(optional)</span></FieldLabel>
                    <StyledInput
                      name="email" type="email" placeholder="you@example.com"
                      value={form.watch('email') || ''}
                      focusedField={focusedField}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('email', e.target.value, form.setValue)}
                      data-testid="input-email"
                      disabled={false}
                    />
                    {validationErrors.email
                      ? <ErrorMsg>{validationErrors.email}</ErrorMsg>
                      : <Hint>Used for schedule updates only</Hint>}
                  </div>
                </div>
              </div>

              {/* ── Academic Details ─────────────────── */}
              <div style={{ marginBottom: 24 }}>
                <SectionTitle icon={BookOpen}>
                  Academic Details
                  {isDataLoading && (
                    <Loader2 size={14} style={{ marginLeft: 8, color: 'rgba(255,255,255,.3)', animation: 'spin .7s linear infinite' }} aria-hidden />
                  )}
                </SectionTitle>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Category */}
                  <FormField control={form.control} name="category" render={({ field }) => (
                    <FormItem>
                      <FieldLabel>PROGRAM CATEGORY <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></FieldLabel>
                      <Select
                        onValueChange={v => { field.onChange(v); setSelectedCategory(v); resetDependentFields('category'); clearFieldError('category'); }}
                        value={field.value}
                        disabled={isDataLoading}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-category" style={selectTriggerStyle(validationErrors.category)}>
                            <SelectValue placeholder={isDataLoading ? 'Loading…' : 'Select category'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="bachelor">Bachelor Programs</SelectItem>
                          <SelectItem value="master">Master Programs</SelectItem>
                        </SelectContent>
                      </Select>
                      {validationErrors.category && <ErrorMsg>{validationErrors.category}</ErrorMsg>}
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* Program */}
                  <FormField control={form.control} name="program" render={({ field }) => (
                    <FormItem>
                      <FieldLabel>PROGRAM <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></FieldLabel>
                      <Select
                        onValueChange={v => { field.onChange(v); setSelectedProgram(v); resetDependentFields('program'); clearFieldError('program'); }}
                        value={field.value}
                        disabled={!selectedCategory || isDataLoading}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-program" style={selectTriggerStyle(validationErrors.program)}>
                            <SelectValue placeholder={!selectedCategory ? 'Select category first' : availablePrograms.length === 0 ? 'No programs' : 'Select program'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availablePrograms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {validationErrors.program
                        ? <ErrorMsg>{validationErrors.program}</ErrorMsg>
                        : selectedCategory && !isDataLoading && <Hint>{availablePrograms.length} program{availablePrograms.length !== 1 ? 's' : ''} available</Hint>}
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* Year */}
                  <FormField control={form.control} name="year" render={({ field }) => (
                    <FormItem>
                      <FieldLabel>ACADEMIC YEAR <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></FieldLabel>
                      <Select
                        onValueChange={v => { field.onChange(v); setSelectedYear(v); resetDependentFields('year'); clearFieldError('year'); }}
                        value={field.value}
                        disabled={!selectedProgram || isDataLoading}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-year" style={selectTriggerStyle(validationErrors.year)}>
                            <SelectValue placeholder={!selectedProgram ? 'Select program first' : 'Select year'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {validationErrors.year && <ErrorMsg>{validationErrors.year}</ErrorMsg>}
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* Semester */}
                  <FormField control={form.control} name="semester" render={({ field }) => (
                    <FormItem>
                      <FieldLabel>SEMESTER <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></FieldLabel>
                      <Select
                        onValueChange={v => { field.onChange(v); setSelectedSemester(v); resetDependentFields('semester'); clearFieldError('semester'); }}
                        value={field.value}
                        disabled={!selectedYear || isDataLoading}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-semester" style={selectTriggerStyle(validationErrors.semester)}>
                            <SelectValue placeholder={!selectedYear ? 'Select year first' : 'Select semester'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableSemesters.map(s => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {validationErrors.semester && <ErrorMsg>{validationErrors.semester}</ErrorMsg>}
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* Group — half width */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginTop: 12 }}>
                  <FormField control={form.control} name="group" render={({ field }) => (
                    <FormItem>
                      <FieldLabel>GROUP <span style={{ color: 'rgba(239,68,68,.6)' }}>*</span></FieldLabel>
                      <Select
                        onValueChange={v => { field.onChange(v); clearFieldError('group'); }}
                        value={field.value}
                        disabled={!selectedSemester || isDataLoading}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-group" style={selectTriggerStyle(validationErrors.group)}>
                            <SelectValue placeholder={!selectedSemester ? 'Select semester first' : 'Select group'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableGroups.map(g => <SelectItem key={g} value={g}>Group {g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {validationErrors.group
                        ? <ErrorMsg>{validationErrors.group}</ErrorMsg>
                        : selectedSemester && !isDataLoading && availableGroups.length > 0
                          && <Hint>{availableGroups.length} group{availableGroups.length !== 1 ? 's' : ''} available</Hint>}
                      <FormMessage />
                    </FormItem>
                  )} />
                  <div /> {/* spacer */}
                </div>
              </div>

              {/* Complete banner */}
              {isComplete && (
                <div style={{
                  background: 'rgba(20,184,166,.06)',
                  border: '1px solid rgba(20,184,166,.2)',
                  borderRadius: 10, padding: '12px 16px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  marginBottom: 16,
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(20,184,166,.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#14B8A6', flexShrink: 0,
                  }}>
                    <Check size={12} aria-hidden />
                  </div>
                  <span style={{ fontSize: 12, color: 'rgba(20,184,166,.85)', fontWeight: 300 }}>
                    Profile is complete and ready to submit
                  </span>
                </div>
              )}

              {/* Data persistence info */}
              <div style={{
                background: 'rgba(255,255,255,.02)',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 10, 
                padding: '14px 16px',
                marginBottom: 16,
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 10,
                  cursor: 'pointer',
                }}>
                  <button
                    type="button"
                    onClick={() => setRememberMe(!rememberMe)}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: `2px solid ${rememberMe ? '#14B8A6' : 'rgba(255,255,255,.2)'}`,
                      background: rememberMe ? '#14B8A6' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {rememberMe && <Check size={12} color="#0A0A0A" aria-hidden />}
                  </button>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: 12, 
                      fontWeight: 500, 
                      color: 'rgba(255,255,255,.8)',
                      marginBottom: 4,
                    }}>
                      Remember my information
                    </div>
                    <div style={{ 
                      fontSize: 10, 
                      color: 'rgba(255,255,255,.35)', 
                      lineHeight: 1.5,
                      fontWeight: 300,
                    }}>
                      Save my profile data on this device so I don't have to fill this form again. Data is stored locally and never shared.
                    </div>
                  </div>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitStatus === 'submitting' || submitStatus === 'validating' || isDataLoading || Object.keys(validationErrors).length > 0}
                onClick={() => {
                  console.log('Button clicked!');
                  console.log('Submit status:', submitStatus);
                  console.log('Validation errors:', validationErrors);
                  console.log('Is data loading:', isDataLoading);
                  console.log('Form values:', form.getValues());
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#14B8A6',
                  border: '1px solid rgba(20, 184, 166, 0.5)',
                  borderRadius: 0,
                  padding: 14, fontSize: 13, fontWeight: 500,
                  fontFamily: "'Outfit',sans-serif", 
                  cursor: (submitStatus === 'submitting' || submitStatus === 'validating' || isDataLoading) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: (submitStatus === 'submitting' || submitStatus === 'validating' || isDataLoading) ? .4 : 1,
                  transition: 'background .2s, opacity .2s',
                }}
                onMouseEnter={e => { 
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.background = 'rgba(20, 184, 166, 0.1)';
                  }
                }}
                onMouseLeave={e => { 
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {(submitStatus === 'submitting') && <><Loader2 size={14} style={{ animation: 'spin .7s linear infinite' }} aria-hidden /> Creating profile…</>}
                {(submitStatus === 'validating') && <><Loader2 size={14} style={{ animation: 'spin .7s linear infinite' }} aria-hidden /> Validating…</>}
                {(submitStatus === 'success') && <><Check size={14} aria-hidden /> Success! Redirecting…</>}
                {(submitStatus === 'none' || submitStatus === 'error') && 'Continue to Dashboard →'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,.2)', marginTop: 10 }}>
                Your timetable profile stays on this device
              </p>

            </form>
          </Form>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Select trigger style helper ─────────────────────────────────────────────
function selectTriggerStyle(hasError: boolean | string) {
  return {
    width: '100%',
    background: 'rgba(255,255,255,.05)',
    border: `1px solid ${hasError ? 'rgba(239,68,68,.5)' : 'rgba(255,255,255,.09)'}`,
    borderRadius: 9, padding: '10px 14px',
    fontSize: 12, color: '#fff',
    fontFamily: "'Outfit',sans-serif", fontWeight: 300,
    outline: 'none',
  };
}
